import { ReactComponent as DocIcon } from 'assets/icons/document.svg'
import { ReactComponent as RestrictedIcon } from 'assets/icons/private16.svg'
import { useLastEditedFormatted } from 'common/Prose/hooks/useLastEditedFormatted'
import { mentionsConversionOptions } from 'common/Prose/utils/mentionsConversionOptions'
import { MentionSubject } from 'common/Prose/utils/smartLinks/types'
import { MentionsFormatBar } from 'components/MentionsFormatBar/MentionsFormatBar'
import { useArticle } from 'hooks/queries/articles/useArticle'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getArticleUrl } from 'utils/urlUtils'
import { type childCardProps } from '../../Card'
import Styles from '../../Card.module.scss'
import { CardViewComponent } from '../CardViewComponent/CardViewComponent'
import { FrameViewComponent } from '../FrameViewComponent/FrameViewComponent'

export const ArticleCard = (props: childCardProps) => {
  const {
    nodeViewContext,
    isSelected,
    isCommentSelected,
    tasks,
    comments,
    currentWorkspaceId,
    showMentionFormatBar,
    setShowMentionFormatBar,
  } = props
  const { node, getPos, view } = nodeViewContext
  const { key, title } = node.attrs
  const url = getArticleUrl(key)
  const [mentionSubject, setMentionSubject] = useState<MentionSubject>({
    title: title,
    avatar: { emoji: <DocIcon className="docEmoji" /> },
  })
  const containerRef = useRef<HTMLDivElement>(null)

  const oneMinute = 60 * 1000
  const {
    data: articleData,
    isError,
    isLoading,
    isLoadingError,
  } = // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useArticle(key!, { staleTime: oneMinute, refetchInterval: oneMinute })

  useEffect(() => {
    if (!articleData) return
    const { header_emoji, title: remoteTitle, last_edited_by, last_edited_at } = articleData.article

    if (title !== remoteTitle) {
      view.dispatch(view.state.tr.setNodeMarkup(getPos(), undefined, { ...node.attrs, title: remoteTitle }))
    }

    setMentionSubject({
      avatar: { emoji: header_emoji ?? <DocIcon className="docEmoji" /> },
      title: remoteTitle,
      lastEditedAt: last_edited_at,
      lastEditedBy: last_edited_by || undefined,
    })
  }, [node.attrs, title, view, getPos, key, articleData])

  useEffect(() => {
    if (!isError && !isLoadingError) return

    setMentionSubject((prevMentionSubject) => ({
      title: prevMentionSubject.title,
      avatar: { emoji: <RestrictedIcon className="restrictedIcon" /> },
    }))
  }, [isError, isLoadingError])

  const lastEdited = useLastEditedFormatted(mentionSubject)

  const subtitle = useMemo(() => {
    if (isError) return "You don't have access to this doc."
    if (isLoading) return 'Loading...'
    return lastEdited
  }, [isError, isLoading, lastEdited])

  return (
    <div className={Styles.cardWrapper} ref={containerRef}>
      {node.attrs.mode === 'card' ? (
        <CardViewComponent
          cardStyle={node.attrs.cardStyle}
          selected={isSelected}
          commentSelected={isCommentSelected}
          tasks={tasks}
          comments={comments}
          mentionSubject={mentionSubject}
          mentionType={node.attrs.mentionType}
          subtitle={subtitle}
          url={url}
          articleId={key}
          loading={isLoading}
          isError={isError}
        />
      ) : (
        <FrameViewComponent
          nodeViewContext={nodeViewContext}
          tasks={tasks}
          comments={comments}
          mentionSubject={mentionSubject}
          subtitle={subtitle}
          selected={isSelected}
          commentSelected={isCommentSelected}
          workspaceId={currentWorkspaceId}
          memberId={node.attrs.userId}
          showInlineMenu={setShowMentionFormatBar}
          loading={isLoading}
          isError={isError}
        />
      )}
      {showMentionFormatBar && view.editable && (
        <MentionsFormatBar
          view={view}
          close={() => setShowMentionFormatBar(false)}
          getPos={getPos}
          mentionSubject={mentionSubject}
          subtitle={subtitle}
          link={url}
          node={node}
          onLinkClick={() => {}}
          conversionOptions={mentionsConversionOptions(node, getPos(), view, mentionSubject.title ?? '')}
          parentRef={containerRef}
        />
      )}
    </div>
  )
}
